/**
 * Interval poller that never stacks overlapping async ticks.
 * If a prior run is still in flight, the next interval is skipped.
 */

export type GuardedAsyncPoller = {
  start: (immediate?: boolean) => void;
  stop: () => void;
  /** Test / diagnostics — true while run() is awaiting. */
  isInFlight: () => boolean;
  isStopped: () => boolean;
};

export function createGuardedAsyncPoller(options: {
  intervalMs: number;
  run: () => Promise<void>;
  /** When true, stop the poller (e.g. auth expired). */
  shouldStop?: () => boolean;
  setIntervalFn?: typeof setInterval;
  clearIntervalFn?: typeof clearInterval;
}): GuardedAsyncPoller {
  const setIntervalFn = options.setIntervalFn ?? setInterval;
  const clearIntervalFn = options.clearIntervalFn ?? clearInterval;

  let cancelled = false;
  let inFlight = false;
  let intervalId: ReturnType<typeof setInterval> | undefined;

  const stop = () => {
    cancelled = true;
    if (intervalId != null) {
      clearIntervalFn(intervalId as unknown as number);
      intervalId = undefined;
    }
  };

  const tick = async () => {
    if (cancelled || inFlight) return;
    if (options.shouldStop?.()) {
      stop();
      return;
    }
    inFlight = true;
    try {
      await options.run();
    } catch {
      // Callers should handle errors inside run; never leave inFlight stuck.
    } finally {
      inFlight = false;
    }
  };

  return {
    start(immediate = true) {
      cancelled = false;
      if (immediate) void tick();
      intervalId = setIntervalFn(() => {
        void tick();
      }, options.intervalMs);
    },
    stop,
    isInFlight: () => inFlight,
    isStopped: () => cancelled,
  };
}
